import React from 'react';
import PropTypes from 'prop-types';
import style from '@src/js/Style';
import AdaptiveTrait from '../../AdaptiveTrait';
import ReportQuery from './ReportQuery';
import Popup from '../Base/Popup';
import Spoiler from '../Base/Spoiler';
import Table from '../Base/Table';
import DateParser from '../../DateParser';
import NumberFormatter from '../../NumberFormatter';
import SpoilerBody from '../Base/SpoilerBody';
import AmortizationScheduleTable from './AmortizationScheduleTable';

export default class AmortizationScheduleResult extends React.Component {

    constructor(props) {

        super(props);

        new AdaptiveTrait(this);

        this.nodes = {
            popup: null,
        };
    }

    static get propTypes() {

        return {
            children: PropTypes.any,
            className: PropTypes.string,
            table: PropTypes.instanceOf(AmortizationScheduleTable),
        };
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER
    //------------------------------------------------------------------------------------------------------------------

    renderComponent(years) {

        return (
            <Popup title='Amortization schedule' ref={ (node) => { this.nodes.popup = node; } }>

                {/* we don't have a pdf design */}
                {/* <div className={ style('flex justify-end w-full mb-4') }>
                    <Button className={ style('button-success-outline') }>
                        <Translation tag='ui.download.pdf' />
                    </Button>
                </div> */}

                <div ref={ (node) => { this.nodes.years = node; } }>
                    { years }
                </div>
                
            </Popup>
        );
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER DESKTOP
    //------------------------------------------------------------------------------------------------------------------

    renderDesktop() {

        return this.renderComponent(this.renderDesktopYears(this.props.table));
    }

    renderDesktopYears(table) {

        const years = Object.values(table.years).map((year) => {

            const rows = year.rows.map((row, rowId) => {
            
                return (
                    <tr className={ null } data-collapse={ false } key={ `row${year.id}-${rowId}` }>
                        <td className={ style('text-bold') }>{ row.paymentDate.getFullMonth() }</td>
                        <td>{ row.payment.formatted() }</td>
                        <td>{ row.paymentPercent.formatted() }</td>
                        <td>{ row.paymentWithoutPercent.formatted() }</td>
                        <td colSpan={ 2 }>{ row.endingBalance.formatted() }</td>
                    </tr>
                );
            });

            let spoilerTarget = null;
            return (<React.StrictMode key={ year.id }>

                <tbody className={ style('table-items-center whitespace-nowrap') }>
                    <tr className={ style('text-muted' )}>
                        <td className={ style('text-medium') }>{ year.title }</td>
                        <td className={ style('text-bold') }>{ year.details.payment.formatted() }</td>
                        <td className={ style('text-bold') }>{ year.details.paymentPercent.formatted() }</td>
                        <td className={ style('text-bold') }>{ year.details.paymentWithoutPercent.formatted() }</td>
                        <td className={ style('text-bold') }>{ year.details.endingBalance.formatted() }</td>
                        <td>
                            <Spoiler target={ () => spoilerTarget } title='' closeOthers={ true }></Spoiler>
                        </td>
                    </tr>
                </tbody>

                <tbody
                    ref={ (node) => { spoilerTarget = node; } }
                    className={ style('table-items-center whitespace-nowrap hidden') }>
                        { rows }
                </tbody>

            </React.StrictMode>);
        });

        return (
            <Table responsible={ false }>
                <thead>
                    <tr className={ style('text-small sticky bg-white' )}>
                        <th>{ table.columns.paymentDate }</th>
                        <th>{ table.columns.payment }</th>
                        <th>{ table.columns.paymentPercent }</th>
                        <th>{ table.columns.paymentWithoutPercent }</th>
                        <th colSpan={ 2 }>{ table.columns.endingBalance }</th>
                    </tr>
                </thead>
                { years }
            </Table>
        );
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER MOBILE
    //------------------------------------------------------------------------------------------------------------------

    renderMobile() {

        return this.renderComponent(this.renderMobileYears(this.props.table));
    }

    renderMobileYears(table) {

        return Object.values(table.years).map((year, id) => {

            const spoilerProps = {

                title: year.title ,
                className: style('title4'),
                closeOthers: this.nodes.years,
                isOpen: id == 0,
            };

            return (
                <div className={ style('w-full mt-2 mb-2') } key={ `year${year.id}` }>
                    <Spoiler { ...spoilerProps }>

                        <div className={ style('mb-2 border-bottom') }>
                            { this.renderMobileYearGroup(table, year.details, false) }
                        </div>

                        <SpoilerBody>
                            { year.rows.map((row) => this.renderMobileYearGroup(table, row, true)) }
                        </SpoilerBody>

                    </Spoiler>
                </div>
            );
        });
    }

    renderMobileYearGroup(table, details, isInner) {

        let classes = 'flex flex-wrap w-full gap py-2';
        if (isInner)
            classes = `${classes} pl-4 even:bg-gray-light-alt`;

        return (
            <div className={ style(classes) } key={ `row${details.id}` }>

                {/* display month for nested blocks */}
                {
                    isInner
                    ? this.renderMobileYearRow(table.columns.paymentDate, details.paymentDate.getFullMonth(), true)
                    : null
                }

                {/* amount of payment */}
                { this.renderMobileYearRow(
                    table.columns.payment,
                    details.payment.formatted()
                ) }

                {/* amount of payment percent */}
                { this.renderMobileYearRow(
                    table.columns.paymentPercent,
                    details.paymentPercent.formatted()
                ) }

                {/* amount of payment without percent */}
                { this.renderMobileYearRow(
                    table.columns.paymentWithoutPercent,
                    details.paymentWithoutPercent.formatted()
                ) }

                {/* ending balance */}
                { this.renderMobileYearRow(
                    table.columns.endingBalance,
                    details.endingBalance.formatted()
                ) }

            </div>
        );
    }

    renderMobileYearRow(label, value, isBold = false) {

        return (
            <div className={ style('flex w-full justify-between items-start') }>
                <div className={ style('w-6/12 text-bold text-muted') }>{ label }</div>
                <div className={ style(`w-6/12 text-right whitespace-nowrap ${isBold ? 'title4' : ''}`.trim()) }>
                    { value }
                </div>
            </div>
        );
    }

    //------------------------------------------------------------------------------------------------------------------
    // ACTIONS
    //------------------------------------------------------------------------------------------------------------------

    show() {

        this.nodes.popup.show();
    }

    hide() {

        this.nodes.popup.hide();
    }

}